/**
 * Market Research Comparison Route Definitions
 *
 * This file contains UI-Router state definitions for comparison-enabled routes
 * with query-based category parameters for multi-category analysis.
 *
 */

import { onEnterRemoveComparedDuration } from "app/pages/industry-analysis/config/onEnterRemoveComparedDuration";
import { onEnterValidateParams } from "app/pages/industry-analysis/config/onEnterValidateParams";
import { onRedirectUnsupportedDuration } from "app/pages/industry-analysis/config/onRedirectUnsupportedDuration";
import {
    marketAnalysisPageViewTracking,
    marketAnalysisPlayersPageViewTracking,
} from "app/pages/industry-analysis/config/pageViewTracking";
import { resolveConfigIdWorldwideWrapper } from "app/pages/industry-analysis/config/resolveConfigId";
import { redirectPipe } from "app/routes/handlers/redirectFunctions/redirectPipe";
import { FiltersEnum } from "app/components/filters-bar/utils";
import { SecondarySideBarType3 } from "app/components/sidebar3/types";
import { onPdfDownload } from "app/pages/industry-analysis/common/onPdfDownload";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";

import { WithNoCustomIndustryHOC } from "../common/WithNoCustomIndustryHOC";
import {
    INDUSTRY_SEGMENTATION_CONFIG_ID,
    MARKET_PLAYERS_CONFIG_ID,
    MARKET_PLAYERS_WITH_SEGMENTS_CONFIG_ID,
    MARKET_RESEARCH_PERFORMANCE_CONFIG_ID,
    MARKET_RESEARCH_PERFORMANCE_WITH_SEGMENTS_CONFIG_ID,
} from "../config/const";
import { IndustryAnalysisDemographicsContainer } from "../demographics/IndustryAnalysisDemographicsContainer";
import { IndustryAnalysisGeographyContainer } from "../geography/IndustryAnalysisGeographyContainer";
import { EDIT_INDUSTRY_ON_ARRIVAL_PARAM } from "../market-builder/MarketBuilder.types";
import { MarketPlayers } from "../players/MarketPlayers";
import { IndustryAnalysisRoute } from "../routes/IndustryAnalysisRoute";
import { SearchTrendsTable } from "../search-trends/Table";
import { IndustrySegmentation } from "../segmentation/IndustrySegmentation";
import { MarketingChannels } from "../traffic-sources/MarketingChannels/MarketingChannels";

import { MarketResearchOverviewRoute } from "./MarketResearchOverviewRoute";
import { V2_ROUTES } from "./routes";
import { resolveV2DatePickerPreset } from "./routeUtils";

/**
 * Base URL path for market research routes
 */
export const MARKET_RESEARCH_BASE_PATH = "/markets/research";

const marketResearchAbstractParentV2: IRouterState = {
    name: V2_ROUTES.abstractParent,
    abstract: true,
    parent: "digitalsuite_marketresearch",
    url: MARKET_RESEARCH_BASE_PATH,
    configId: "IndustryAnalysis",
    component: IndustryAnalysisRoute,
    secondaryBarType3: SecondarySideBarType3.IndustryResearch,
    homeState: "digitalsuite_webmarketanalysis_home",
    data: {
        menuKbItems: false,
        disableRecording: true,
        filtersConfig: {
            duration: FiltersEnum.ENABLED,
            country: FiltersEnum.ENABLED,
            webSource: FiltersEnum.DISABLED,
        },
    },
    pageId: {
        section: "industryAnalysis",
    },
    trackingId: {
        section: "Market Research",
        subSection: "Market Analysis V2",
    },
    navBarContainerClassName: "accordion-side-bar",
    isModuleSupportSegments: true,
    entityType: "industry" as const,
};

/**
 * Shared defaults for comparison routes
 */
const v2RouteDefaults = {
    parent: V2_ROUTES.abstractParent,
    secondaryBarType3: SecondarySideBarType3.IndustryResearch,
    homeState: "digitalsuite_webmarketanalysis_home",
    onEnter: onEnterValidateParams,
    onRetain: onEnterValidateParams,
    params: {
        comparedDuration: "",
        selectedMetric: { dynamic: true },
    },
    defaultQueryParams: {
        webSource: "Total",
        duration: "13m",
    },
    data: {
        menuKbItems: false,
        disableRecording: true,
        filtersConfig: {
            duration: FiltersEnum.ENABLED,
            country: FiltersEnum.ENABLED,
            webSource: FiltersEnum.DISABLED,
        },
    },
};

const marketResearchOverviewV2: IRouterState = {
    ...v2RouteDefaults,
    name: V2_ROUTES.overview,
    url: "/overview?category&country&duration&webSource&selectedMetric&comparedDuration&includeSubdomains",
    component: MarketResearchOverviewRoute,
    stateNameForEductionBar: "marketresearch_webmarketResearch_overview",
    get configId() {
        return resolveConfigIdWorldwideWrapper(
            MARKET_RESEARCH_PERFORMANCE_CONFIG_ID,
            MARKET_RESEARCH_PERFORMANCE_WITH_SEGMENTS_CONFIG_ID,
        );
    },
    configIdDeps: ["MobileWeb", "CustomSegments", "IndustryAnalysisOverview"],
    pageTitle: "industryOverview.performance.page.title",
    trackingId: {
        section: "Market Research",
        subSection: "Market Analysis",
        subSubSection: "MarketResearchOverview",
    },
    get overrideDatepickerPreset() {
        return resolveV2DatePickerPreset();
    },
    params: {
        ...v2RouteDefaults.params,
        category: {
            type: "string",
            array: true,
            dynamic: true,
        },
        comparedDuration: "",
        selectedMetric: { dynamic: true },
        includeSubdomains: { type: "bool", value: true },
        [EDIT_INDUSTRY_ON_ARRIVAL_PARAM]: {
            value: null,
            dynamic: true,
            inherit: false,
        },
    },
    data: {
        stateNameForEductionBar: "marketresearch_webmarketResearch_overview",
    },
    get isPdfDownloadButton() {
        const { category } = proNavigator.getParams();
        return !(Array.isArray(category) && category.length > 1);
    },
    pdfDownloadsMethod: onPdfDownload,
    segmentsSupport: true,
    periodOverPeriodEnabled: true,
};

const marketResearchTrafficChannelsV2: IRouterState = {
    ...v2RouteDefaults,
    name: V2_ROUTES.trafficChannels,
    url: "/traffic-channels?category&country&duration&webSource",
    component: MarketingChannels,
    stateNameForEductionBar: "marketresearch_webmarketanalysis_trafficChannels",
    configId: "IndustryAnalysisMarketingChannels",
    configIdDeps: ["IndustryAnalysisMarketingChannels"],
    onEnter: onEnterRemoveComparedDuration,
    params: {
        ...v2RouteDefaults.params,
        comparedDuration: {
            value: null,
            squash: true,
        },
    },
    redirectTo: redirectPipe(onRedirectUnsupportedDuration()),
    pageId: {
        section: "industryAnalysis",
        subSection: "traffic",
        subSubSection: "comparison",
    },
    trackingId: {
        section: "Market Research",
        subSection: "Market Analysis",
        subSubSection: "trafficChannels",
    },
    clearSearch: true,
    pageTitle: "digitalsuite.industryAnalysis.trafficchannels.page.title",
    get overrideDatepickerPreset() {
        return resolveV2DatePickerPreset();
    },
    defaultQueryParams: {
        country: "999",
    },
    data: {
        ...v2RouteDefaults.data,
        filtersConfig: {
            ...v2RouteDefaults.data.filtersConfig,
            webSource: FiltersEnum.DISABLED,
        },
    },
    periodOverPeriodEnabled: false,
    segmentsSupport: true,
};

const marketResearchGeographyV2: IRouterState = {
    ...v2RouteDefaults,
    name: V2_ROUTES.geography,
    url: "/geography?category&duration&webSource",
    component: IndustryAnalysisGeographyContainer,
    stateNameForEductionBar: "marketresearch_webmarketanalysis_geography",
    configId: "MarketResearchGeographics",
    onEnter: onEnterRemoveComparedDuration,
    params: {
        ...v2RouteDefaults.params,
        comparedDuration: {
            value: null,
            squash: true,
        },
    },
    pageId: {
        section: "industryAnalysis",
        subSection: "geography",
        subSubSection: "comparison",
    },
    trackingId: {
        section: "Market Research",
        subSection: "Market Analysis",
        subSubSection: "geography",
    },
    clearSearch: true,
    pageTitle: "digitalsuite.industryAnalysis.geography.page.title",
    pageSubtitle: "industryAnalysis.geography.page.subtitle",
    data: {
        ...v2RouteDefaults.data,
        filtersConfig: {
            ...v2RouteDefaults.data.filtersConfig,
            country: FiltersEnum.HIDDEN,
        },
    },
    skipCountryCheck: true,
    periodOverPeriodEnabled: false,
    segmentsSupport: false,
};

const marketResearchDemographyV2: IRouterState = {
    ...v2RouteDefaults,
    name: V2_ROUTES.demography,
    url: "/demographics?category&country&duration&webSource",
    component: IndustryAnalysisDemographicsContainer,
    stateNameForEductionBar: "marketresearch_webmarketanalysis_demographics",
    configId: "MarketResearchDemographics",
    configIdDeps: ["MobileWeb"],
    onEnter: onEnterRemoveComparedDuration,
    params: {
        ...v2RouteDefaults.params,
        comparedDuration: {
            value: null,
            squash: true,
        },
    },
    pageId: {
        section: "industryAnalysis",
        subSection: "overview",
        subSubSection: "demographicsComparison",
    },
    trackingId: {
        section: "Market Research",
        subSection: "Market Analysis",
        subSubSection: "demographics",
    },
    pageTitle: "industryAnalysis.demographics.page.title",
    clearSearch: true,
    periodOverPeriodEnabled: false,
    segmentsSupport: false,
};

export const marketResearchPlayersV2: IRouterState = {
    ...v2RouteDefaults,
    name: V2_ROUTES.players,
    // eslint-disable-next-line max-len
    url: "/players?category&country&duration&comparedDuration&webSource&bubbleChartDisplayType&bubbleChartShowAverage&marketSharesDisplayType&marketSharesChartType&marketSharesTimeGranularity&scrollTo&useDomainsOnly&includeRegionalDomains",
    component: MarketPlayers,
    stateNameForEductionBar: "marketresearch_webmarketanalysis_players",
    get configId() {
        return resolveConfigIdWorldwideWrapper(MARKET_PLAYERS_CONFIG_ID, MARKET_PLAYERS_WITH_SEGMENTS_CONFIG_ID);
    },
    configIdDeps: ["MobileWeb", "IndustryAnalysis", MARKET_PLAYERS_WITH_SEGMENTS_CONFIG_ID],
    pageId: {
        section: "industryAnalysis",
        subSection: "market players",
    },
    trackingId: {
        section: "Market Research",
        subSection: "Market Analysis",
        subSubSection: "Market Players",
    },
    pageTitle: "industry.research.pages.market.players.title",
    pageSubtitle: "industry.research.pages.market.players.title.tooltip",
    get overrideDatepickerPreset() {
        return resolveV2DatePickerPreset();
    },
    params: {
        ...v2RouteDefaults.params,
        category: {
            type: "string",
            array: true,
            dynamic: true,
            raw: true,
        },
        marketSharesDisplayType: { type: "string", dynamic: true },
        marketSharesChartType: { type: "string", dynamic: true },
        marketSharesTimeGranularity: { type: "string", dynamic: true },
        scrollTo: { type: "string" },
        bubbleChartDisplayType: { type: "string", dynamic: true },
        bubbleChartShowAverage: { type: "string", dynamic: true },
        includeRegionalDomains: { type: "string", dynamic: true },
    },
    periodOverPeriodEnabled: true,
    clearSearch: true,
    data: {
        ...v2RouteDefaults.data,
        filtersConfig: {
            ...v2RouteDefaults.data.filtersConfig,
        },
        pageViewTracking: marketAnalysisPlayersPageViewTracking,
    },
    segmentsSupport: true,
};

export const marketResearchSegmentationV2: IRouterState = {
    ...v2RouteDefaults,
    name: V2_ROUTES.segmentation,
    url: "/segmentation?category&country&duration&webSource&comparedDuration&overTimeChartDisplayType&growthOverviewMetric&growthOverviewGranularity",
    component: WithNoCustomIndustryHOC(IndustrySegmentation),
    stateNameForEductionBar: "marketresearch_webmarketanalysis_segmentation",
    configId: INDUSTRY_SEGMENTATION_CONFIG_ID,
    configIdDeps: ["MobileWeb", "CustomSegments"],
    pageId: {
        section: "industryAnalysis",
        subSection: "segmentation",
        subSubSection: "comparison",
    },
    trackingId: {
        section: "Market Research",
        subSection: "Market Analysis",
        subSubSection: "marketSegments",
    },
    pageTitle: "industryAnalysis.segmentation.page.title",
    pageSubtitle: "industryAnalysis.segmentation.page.tooltip",
    params: {
        ...v2RouteDefaults.params,
        category: {
            type: "string",
            array: true,
            dynamic: true,
            raw: true,
        },
        overTimeChartDisplayType: { type: "string", dynamic: true },
        growthOverviewMetric: { type: "string", dynamic: true },
        growthOverviewGranularity: { type: "string", dynamic: true },
    },
    periodOverPeriodEnabled: true,
    data: {
        ...v2RouteDefaults.data,
        pageViewTracking: marketAnalysisPageViewTracking,
    },
    segmentsSupport: false,
};

export const marketResearchDemandTrendsV2: IRouterState = {
    ...v2RouteDefaults,
    name: V2_ROUTES.demandTrends,
    // eslint-disable-next-line max-len
    url: "/search-trends?category&country&duration&tab&channel&includeBranded&includeNoneBranded&excludeBranded&orderBy&search&BooleanSearchTerms&webSource&IncludeTrendingKeywords&IncludeNewKeywords&IncludeQuestions",
    component: SearchTrendsTable,
    stateNameForEductionBar: "marketresearch_webmarketanalysis_searchtrends",
    configId: "IndustrySearchInterests",
    configIdDeps: ["KeywordAnalysis"],
    pageId: {
        section: "industryAnalysis",
        subSection: "audience",
        subSubSection: "searchTrends",
    },
    trackingId: {
        section: "Market Research",
        subSection: "Market Analysis",
        subSubSection: "searchTrends",
    },
    clearSearch: true,
    pageTitle: "industryAnalysis.demand.trends.title",
    pageSubtitle: "industryAnalysis.searchTrends.subtitle",
    params: {
        ...v2RouteDefaults.params,
        category: {
            type: "string",
            array: true,
            dynamic: true,
            raw: true,
        },
        includeNoneBranded: "true",
        includeBranded: "false",
        tab: { dynamic: true },
    },
    data: {
        ...v2RouteDefaults.data,
        filtersConfig: {
            ...v2RouteDefaults.data.filtersConfig,
            webSource: FiltersEnum.DISABLED,
        },
        pageViewTracking: marketAnalysisPageViewTracking,
    },
    segmentsSupport: false,
};

/**
 * All V2 route definitions
 */
const ALL_V2_ROUTES = [
    marketResearchOverviewV2,
    marketResearchTrafficChannelsV2,
    marketResearchGeographyV2,
    marketResearchDemographyV2,
    marketResearchPlayersV2,
    marketResearchSegmentationV2,
    marketResearchDemandTrendsV2,
] as const;

/**
 * Get all V2 routes - always enabled now that feature flags are removed
 */
export const getEnabledV2Routes = (): Record<string, IRouterState> => {
    const allRoutes = ALL_V2_ROUTES.reduce(
        (acc, route) => ({ ...acc, [route.name]: route }),
        {} as Record<string, IRouterState>,
    );

    return {
        [marketResearchAbstractParentV2.name]: marketResearchAbstractParentV2,
        ...allRoutes,
    };
};
